/**
 * MinistrareIA - Message Media Components
 * Renders different media types in chat messages (WhatsApp-style)
 * 
 * Supports:
 * - Images: Thumbnail preview + fullscreen lightbox
 * - Audio: Waveform player with play/pause
 * - Video: Thumbnail + play overlay
 * - Documents: Icon + filename + download
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  Play,
  Pause,
  Download,
  X,
  Loader2,
  File,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  AlertCircle,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  
  // Handle object format from WhatsApp { low: number, high: number }
  const size = typeof bytes === 'object' ? bytes.low || 0 : bytes;
  
  if (size === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Format duration in seconds to mm:ss
 */
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get document icon based on mimetype
 */
const getDocumentIcon = (mimetype, fileName) => {
  const mime = (mimetype || '').toLowerCase();
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  
  if (mime.includes('pdf') || ext === 'pdf') {
    return { icon: FileText, color: '#E53935', label: 'PDF' };
  }
  if (mime.includes('word') || ['doc', 'docx'].includes(ext)) {
    return { icon: FileText, color: '#2196F3', label: 'DOC' };
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) {
    return { icon: FileText, color: '#4CAF50', label: 'XLS' };
  }
  if (mime.includes('powerpoint') || mime.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
    return { icon: FileText, color: '#FF9800', label: 'PPT' };
  }
  if (mime.includes('zip') || mime.includes('rar') || ['zip', 'rar', '7z'].includes(ext)) {
    return { icon: File, color: '#9C27B0', label: 'ZIP' };
  }
  if (mime.includes('text') || ext === 'txt') {
    return { icon: FileText, color: '#607D8B', label: 'TXT' };
  }
  
  return { icon: File, color: '#757575', label: 'FILE' };
};

// ============================================
// IMAGE MESSAGE COMPONENT
// ============================================

export const ImageMessage = ({ 
  message, 
  isSent,
  onOpenLightbox,
  onDownload,
}) => {
  const { colors } = useTheme();
  const imageMessage = message.message?.imageMessage;
  
  if (!imageMessage) return null;
  
  const thumbnail = imageMessage.jpegThumbnail;
  const caption = imageMessage.caption;
  const width = imageMessage.width || 300;
  const height = imageMessage.height || 200;
  
  // Calculate aspect ratio for display
  const aspectRatio = width / height;
  const displayWidth = Math.min(280, width);
  const displayHeight = displayWidth / aspectRatio;
  
  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 280,
    },
    image: {
      width: displayWidth,
      height: Math.min(displayHeight, 320),
      objectFit: 'cover',
      display: 'block',
      backgroundColor: colors.bgTertiary,
    },
    placeholder: {
      width: displayWidth,
      height: Math.min(displayHeight, 200),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.4))',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      padding: 8,
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
    zoomIcon: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    caption: {
      padding: '8px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
    },
  };

  const handleClick = () => {
    if (onOpenLightbox) {
      onOpenLightbox({
        type: 'image',
        thumbnail,
        messageKey: message.key,
        caption,
      });
    }
  };

  return (
    <div>
      <div 
        style={styles.container}
        onClick={handleClick}
        onMouseEnter={(e) => {
          const overlay = e.currentTarget.querySelector('.image-overlay');
          if (overlay) overlay.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const overlay = e.currentTarget.querySelector('.image-overlay');
          if (overlay) overlay.style.opacity = '0';
        }}
      >
        {thumbnail ? (
          <img 
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={caption || 'Imagem'}
            style={styles.image}
          />
        ) : (
          <div style={styles.placeholder}>
            <ImageIcon size={48} />
          </div>
        )}
        <div className="image-overlay" style={styles.overlay}>
          <div style={styles.zoomIcon}>
            <ZoomIn size={18} />
          </div>
        </div>
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// AUDIO MESSAGE COMPONENT
// ============================================

export const AudioMessage = ({ 
  message, 
  isSent,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  
  const audioMessage = message?.message?.audioMessage;
  const isPTT = audioMessage?.ptt; // Voice note (push-to-talk)
  const waveformData = audioMessage?.waveform;
  const audioDuration = audioMessage?.seconds || 0;
  
  // Parse waveform data (base64 encoded) - useMemo to avoid recalculating
  const waves = useMemo(() => {
    if (!waveformData) {
      // Generate fake waveform
      return Array(32).fill(0).map(() => Math.random() * 0.8 + 0.2);
    }
    try {
      const decoded = atob(waveformData);
      const values = [];
      for (let i = 0; i < decoded.length; i++) {
        values.push(decoded.charCodeAt(i) / 255);
      }
      return values.length > 0 ? values : Array(32).fill(0.5);
    } catch {
      return Array(32).fill(0.5);
    }
  }, [waveformData]);
  
  const progress = duration > 0 ? currentTime / duration : 0;

  // Audio element event listeners
  useEffect(() => {
    if (audioRef.current && audioSrc) {
      const audio = audioRef.current;
      
      const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
      const handleLoadedMetadata = () => setDuration(audio.duration);
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioSrc]);

  // Early return AFTER all hooks
  if (!audioMessage) return null;

  const handlePlayPause = async () => {
    if (isLoading) return;
    
    if (!audioSrc) {
      // Need to load audio first
      setIsLoading(true);
      setError(null);
      
      try {
        // For now, show that we need to download
        // In production, this would call the backend to get base64
        if (onDownload) {
          const result = await onDownload(message.key, 'audio');
          if (result?.base64) {
            const src = `data:${result.mimetype || 'audio/ogg'};base64,${result.base64}`;
            setAudioSrc(src);
            setDuration(audioDuration || result.duration || 0);
          } else {
            throw new Error('Não foi possível carregar o áudio');
          }
        } else {
          // Use placeholder duration
          setDuration(audioDuration);
          setError('Áudio não disponível');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 12px',
      minWidth: 200,
      maxWidth: 320,
    },
    playButton: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: isSent ? 'rgba(255,255,255,0.2)' : colors.accentPrimary,
      color: isSent ? colors.messageSentText : 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'transform 0.1s ease',
    },
    waveformContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    waveform: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: 28,
    },
    bar: {
      width: 3,
      borderRadius: 2,
      transition: 'background-color 0.1s ease',
    },
    timeContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 11,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    pttIndicator: {
      fontSize: 10,
      color: isSent ? 'rgba(255,255,255,0.7)' : colors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  };

  return (
    <div style={styles.container}>
      {audioSrc && <audio ref={audioRef} src={audioSrc} preload="metadata" />}
      
      {/* Play/Pause Button */}
      <button 
        style={styles.playButton}
        onClick={handlePlayPause}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        ) : isPlaying ? (
          <Pause size={20} />
        ) : (
          <Play size={20} style={{ marginLeft: 2 }} />
        )}
      </button>
      
      {/* Waveform */}
      <div style={styles.waveformContainer}>
        <div style={styles.waveform}>
          {waves.slice(0, 32).map((value, index) => {
            const isPlayed = (index / waves.length) <= progress;
            const barColor = isPlayed 
              ? (isSent ? 'rgba(255,255,255,0.9)' : colors.accentPrimary)
              : (isSent ? 'rgba(255,255,255,0.4)' : colors.textTertiary);
            
            return (
              <div
                key={index}
                style={{
                  ...styles.bar,
                  height: `${Math.max(value * 24, 4)}px`,
                  backgroundColor: barColor,
                }}
              />
            );
          })}
        </div>
        
        {/* Time */}
        <div style={styles.timeContainer}>
          <span>{formatDuration(audioSrc ? currentTime : 0)}</span>
          <span>{formatDuration(duration || audioDuration)}</span>
        </div>
        
        {/* PTT indicator */}
        {isPTT && (
          <div style={styles.pttIndicator}>
            <Music size={10} />
            <span>Mensagem de voz</span>
          </div>
        )}
        
        {/* Error */}
        {error && (
          <div style={{ ...styles.pttIndicator, color: '#ef4444' }}>
            <AlertCircle size={10} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// VIDEO MESSAGE COMPONENT
// ============================================

export const VideoMessage = ({ 
  message, 
  isSent,
  onOpenLightbox,
  onDownload,
}) => {
  const { colors } = useTheme();
  const videoMessage = message.message?.videoMessage;
  
  if (!videoMessage) return null;
  
  const thumbnail = videoMessage.jpegThumbnail;
  const caption = videoMessage.caption;
  const duration = videoMessage.seconds;
  const width = videoMessage.width || 300;
  const height = videoMessage.height || 200;
  
  const aspectRatio = width / height;
  const displayWidth = Math.min(280, width);
  const displayHeight = displayWidth / aspectRatio;
  
  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 280,
    },
    thumbnail: {
      width: displayWidth,
      height: Math.min(displayHeight, 320),
      objectFit: 'cover',
      display: 'block',
      backgroundColor: colors.bgTertiary,
    },
    placeholder: {
      width: displayWidth,
      height: Math.min(displayHeight, 200),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.3)',
    },
    playButton: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '2px solid white',
    },
    duration: {
      position: 'absolute',
      bottom: 8,
      right: 8,
      backgroundColor: 'rgba(0,0,0,0.6)',
      color: 'white',
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 500,
    },
    caption: {
      padding: '8px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
    },
  };

  const handleClick = () => {
    if (onOpenLightbox) {
      onOpenLightbox({
        type: 'video',
        thumbnail,
        messageKey: message.key,
        caption,
        duration,
      });
    }
  };

  return (
    <div>
      <div style={styles.container} onClick={handleClick}>
        {thumbnail ? (
          <img 
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={caption || 'Vídeo'}
            style={styles.thumbnail}
          />
        ) : (
          <div style={styles.placeholder}>
            <Video size={48} />
          </div>
        )}
        <div style={styles.overlay}>
          <div style={styles.playButton}>
            <Play size={28} style={{ marginLeft: 4 }} />
          </div>
        </div>
        {duration && (
          <div style={styles.duration}>
            {formatDuration(duration)}
          </div>
        )}
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// DOCUMENT MESSAGE COMPONENT
// ============================================

export const DocumentMessage = ({ 
  message, 
  isSent,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  
  const documentMessage = message.message?.documentMessage;
  if (!documentMessage) return null;
  
  const fileName = documentMessage.fileName || 'Documento';
  const fileSize = documentMessage.fileLength;
  const mimetype = documentMessage.mimetype;
  
  const { icon: DocIcon, color: iconColor, label } = getDocumentIcon(mimetype, fileName);
  
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      backgroundColor: isSent ? 'rgba(255,255,255,0.1)' : colors.bgTertiary,
      borderRadius: 8,
      minWidth: 220,
      maxWidth: 320,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    },
    iconContainer: {
      width: 44,
      height: 44,
      borderRadius: 8,
      backgroundColor: `${iconColor}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    info: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    fileName: {
      fontSize: 14,
      fontWeight: 500,
      color: isSent ? colors.messageSentText : colors.textPrimary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    downloadBtn: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: isSent ? colors.messageSentText : colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.15s ease',
    },
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloading) return;
    
    setIsDownloading(true);
    try {
      if (onDownload) {
        const result = await onDownload(message.key, 'document');
        if (result?.base64) {
          // Create download link
          const link = document.createElement('a');
          link.href = `data:${result.mimetype || 'application/octet-stream'};base64,${result.base64}`;
          link.download = result.fileName || fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div 
      style={styles.container}
      onClick={handleDownload}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = isSent 
          ? 'rgba(255,255,255,0.15)' 
          : colors.bgSecondary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isSent 
          ? 'rgba(255,255,255,0.1)' 
          : colors.bgTertiary;
      }}
    >
      <div style={styles.iconContainer}>
        <DocIcon size={24} color={iconColor} />
      </div>
      
      <div style={styles.info}>
        <span style={styles.fileName} title={fileName}>
          {fileName}
        </span>
        <div style={styles.meta}>
          <span>{label}</span>
          {fileSize && (
            <>
              <span>•</span>
              <span>{formatFileSize(fileSize)}</span>
            </>
          )}
        </div>
      </div>
      
      <button 
        style={styles.downloadBtn}
        onClick={handleDownload}
        disabled={isDownloading}
        title="Baixar"
      >
        {isDownloading ? (
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Download size={20} />
        )}
      </button>
    </div>
  );
};

// ============================================
// MEDIA LIGHTBOX COMPONENT
// ============================================

export const MediaLightbox = ({
  isOpen,
  onClose,
  media,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [fullMedia, setFullMedia] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (isOpen && media) {
      loadFullMedia();
    } else {
      setFullMedia(null);
      setError(null);
    }
  }, [isOpen, media]);

  const loadFullMedia = async () => {
    if (!media || !onDownload) {
      setError('Mídia não disponível');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await onDownload(media.messageKey, media.type);
      if (result?.base64) {
        setFullMedia({
          src: `data:${result.mimetype || 'image/jpeg'};base64,${result.base64}`,
          mimetype: result.mimetype,
        });
      } else {
        // Use thumbnail as fallback
        if (media.thumbnail) {
          setFullMedia({
            src: `data:image/jpeg;base64,${media.thumbnail}`,
            mimetype: 'image/jpeg',
          });
        } else {
          throw new Error('Não foi possível carregar a mídia');
        }
      }
    } catch (err) {
      setError(err.message);
      // Still show thumbnail if available
      if (media.thumbnail) {
        setFullMedia({
          src: `data:image/jpeg;base64,${media.thumbnail}`,
          mimetype: 'image/jpeg',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.95)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      color: 'white',
    },
    closeBtn: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      overflow: 'hidden',
    },
    media: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
      borderRadius: 8,
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      color: 'white',
    },
    error: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      color: '#ef4444',
    },
    caption: {
      padding: '12px 20px',
      textAlign: 'center',
      color: 'rgba(255,255,255,0.8)',
      fontSize: 14,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.header}>
        <div />
        <button style={styles.closeBtn} onClick={onClose}>
          <X size={24} />
        </button>
      </div>
      
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <div style={styles.loading}>
            <Loader2 size={48} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Carregando mídia...</span>
          </div>
        ) : error && !fullMedia ? (
          <div style={styles.error}>
            <AlertCircle size={48} />
            <span>{error}</span>
          </div>
        ) : fullMedia ? (
          media.type === 'video' ? (
            <video
              src={fullMedia.src}
              controls
              autoPlay
              style={styles.media}
            />
          ) : (
            <img
              src={fullMedia.src}
              alt={media.caption || 'Imagem'}
              style={styles.media}
            />
          )
        ) : null}
      </div>
      
      {media?.caption && (
        <div style={styles.caption}>{media.caption}</div>
      )}
    </div>
  );
};

// ============================================
// MAIN MESSAGE MEDIA COMPONENT
// ============================================

const MessageMedia = ({
  message,
  isSent,
  onDownload,
}) => {
  const [lightboxMedia, setLightboxMedia] = useState(null);
  
  const messageContent = message.message || {};
  
  // Determine media type
  const hasImage = !!messageContent.imageMessage;
  const hasVideo = !!messageContent.videoMessage;
  const hasAudio = !!messageContent.audioMessage;
  const hasDocument = !!messageContent.documentMessage;
  
  const handleOpenLightbox = (media) => {
    setLightboxMedia(media);
  };

  const handleCloseLightbox = () => {
    setLightboxMedia(null);
  };

  return (
    <>
      {hasImage && (
        <ImageMessage
          message={message}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
          onDownload={onDownload}
        />
      )}
      
      {hasVideo && (
        <VideoMessage
          message={message}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
          onDownload={onDownload}
        />
      )}
      
      {hasAudio && (
        <AudioMessage
          message={message}
          isSent={isSent}
          onDownload={onDownload}
        />
      )}
      
      {hasDocument && (
        <DocumentMessage
          message={message}
          isSent={isSent}
          onDownload={onDownload}
        />
      )}
      
      <MediaLightbox
        isOpen={!!lightboxMedia}
        onClose={handleCloseLightbox}
        media={lightboxMedia}
        onDownload={onDownload}
      />
    </>
  );
};

export default MessageMedia;